// IE Hub - Support Service
// Handles Contact Us email and generic button-action logging for support/help flows

import { httpPost } from "../http";
import { customerEndpoints } from "../endpoints";
import { femaCapture } from "../../fema/capture";
import type {
  ApiResponse,
  ButtonActionLogRequest,
  ContactUsRequest,
  ContactUsResponse,
  RequestConfig,
  ServiceResponse,
} from "../types";

export class SupportService {
  /**
   * Send a Contact Us message
   */
  static async sendContactUsMessage(
    payload: ContactUsRequest,
    config?: RequestConfig
  ): ServiceResponse<ContactUsResponse> {
    try {
      const res = await httpPost<ApiResponse<ContactUsResponse>>(
        customerEndpoints.contactUs(),
        payload,
        config
      );

      const maybeEnvelope = res?.data as any;
      const data: any = (maybeEnvelope && (maybeEnvelope.data ?? maybeEnvelope)) || {};

      if (data) {
        femaCapture("support_contact_us_success", { length: payload.message?.length });
        return { success: true, data } as any;
      }

      return { success: false, error: "Empty response" } as any;
    } catch (error: any) {
      femaCapture("support_contact_us_error", { message: error?.message });
      return { success: false, error: error?.message || "Request failed" } as any;
    }
  }

  /**
   * Request account deletion (mirrors legacy IeDeleteAccountEmail/insert)
   */
  static async requestAccountDeletion(
    message: string,
    config?: RequestConfig,
    meta?: { userId?: number | string; customerId?: number | string }
  ): ServiceResponse<{ id?: number }> {
    try {
      // Use the new controller (preferred). No legacy fallback.
      const payload: {
        reason: string;
        userId?: number | string;
        customerId?: number | string;
      } = { reason: message };

      if (meta?.userId !== undefined && meta?.userId !== null) {
        payload.userId = meta.userId;
      }
      if (meta?.customerId !== undefined && meta?.customerId !== null) {
        payload.customerId = meta.customerId;
      }

      const res = await httpPost<ApiResponse<{ id?: number }>>(
        customerEndpoints.createDeleteRequest(),
        payload,
        config
      );

      const maybeEnvelope = res?.data as any;
      const data: any = (maybeEnvelope && (maybeEnvelope.data ?? maybeEnvelope)) || {};
      return { success: true, data } as any;
    } catch (error: any) {
      const errMsg = typeof error?.message === 'string' ? error.message : 'Request failed';
      return { success: false, error: errMsg } as any;
    }
  }

  /**
   * Log a button/action event (best-effort)
   */
  static async logButtonAction(
    payload: ButtonActionLogRequest,
    config?: RequestConfig
  ): ServiceResponse<void> {
    try {
      const requestConfig = { ...(config ?? {}), skipErrorCapture: true };

      await httpPost<ApiResponse<void>>(
        customerEndpoints.logButtonAction(),
        payload,
        requestConfig
      );
      femaCapture("support_button_action_logged", {
        pageName: payload.pageName,
        subSection: payload.subSection,
      });
      return { success: true } as any;
    } catch (error: any) {
      // Best effort: do not fail callers
      femaCapture("support_button_action_error", { message: error?.message });
      return { success: true } as any;
    }
  }
}

export default SupportService;



